/**
 * Type guard functions for runtime type checking of Peacock rails
 */

import { TemplateType } from "../../shared-atom-types";
import type { RailWithTiles } from "../types";

/**
 * Type guard for Highlight template rail
 * @param rail - The rail to check
 * @returns True if the rail uses the HIGHLIGHT template
 */
function isHighlightRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.Highlight;
}

/**
 * Type guard for Vertical View template rail
 * @param rail - The rail to check
 * @returns True if the rail uses the VERTICAL_VIEW template
 */
function isVerticalViewRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.VerticalView;
}

/**
 * Type guard for Horizontal View template rail
 * @param rail - The rail to check
 * @returns True if the rail uses the HORIZONTAL_VIEW template
 */
function isHorizontalViewRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.HorizontalView;
}

/**
 * Type guard for Continue Watching rail
 * @param rail - The rail to check
 * @returns True if the rail uses the CONTINUE_WATCHING template
 */
function isContinueWatchingRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.ContinueWatching;
}

/**
 * Type guard for Watchlist rail
 * @param rail - The rail to check
 * @returns True if the rail uses the WATCHLIST template
 */
function isWatchlistRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.Watchlist;
}

/**
 * Type guard for Showcase template rail
 * @param rail - The rail to check
 * @returns True if the rail uses the SHOWCASE template
 */
function isShowcaseRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.Showcase;
}

/**
 * Type guard for Game Rail template
 * @param rail - The rail to check
 * @returns True if the rail uses the GAME_RAIL template
 */
function isGameRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.Gamerail;
}

/**
 * Type guard for Live template rail
 * @param rail - The rail to check
 * @returns True if the rail uses the LIVE template
 */
function isLiveRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.Live;
}

/**
 * Type guard for Grid template rail
 * @param rail - The rail to check
 * @returns True if the rail uses the GRID template
 */
function isGridRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.template === TemplateType.Grid;
}

/**
 * Type guard for personalized rails
 * @param rail - The rail to check
 * @returns True if the rail is personalized content
 */
function isPersonalizedRail(rail: RailWithTiles): boolean {
  return rail.isPersonalized === true;
}

/**
 * Type guard for numbered rails
 * @param rail - The rail to check
 * @returns True if the rail should display numbers
 */
function isNumberedRail(rail: RailWithTiles): boolean {
  return rail.renderHint?.numberedRail === true;
}

/**
 * Type guard for skipped rails
 * @param rail - The rail to check
 * @returns True if the rail was skipped during fetching
 */
function isSkippedRail(rail: RailWithTiles): boolean {
  return rail.skipped === true;
}

/**
 * Type guard for rails with full tile details
 * @param rail - The rail to check
 * @returns True if the rail has tilesWithDetails populated
 */
function hasDetailedTiles(rail: RailWithTiles): rail is RailWithTiles & {
  tilesWithDetails: NonNullable<RailWithTiles["tilesWithDetails"]>;
} {
  return (
    Array.isArray(rail.tilesWithDetails) && rail.tilesWithDetails.length > 0
  );
}

/**
 * Type guard for rails with errors
 * @param rail - The rail to check
 * @returns True if the rail has an error message
 */
function hasError(
  rail: RailWithTiles,
): rail is RailWithTiles & { error: string } {
  return typeof rail.error === "string" && rail.error.length > 0;
}

export {
  isHighlightRail,
  isVerticalViewRail,
  isHorizontalViewRail,
  isContinueWatchingRail,
  isWatchlistRail,
  isShowcaseRail,
  isGameRail,
  isLiveRail,
  isGridRail,
  isPersonalizedRail,
  isNumberedRail,
  isSkippedRail,
  hasDetailedTiles,
  hasError,
};
